import { useForm } from "@inertiajs/react";
import AppShell from "../components/Shell/AppShell";
import Switch from "../components/Switch";
import { pick } from "../lib/i18n";

const copy = {
  pl: {
    title: "Ustawienia", textModel: "Model tekstowy", textModelField: "Model do parsowania produktu",
    imageModel: "Model obrazu", imageModelField: "Model do generacji grafik",
    defaultResolution: "Domyślna rozdzielczość i orientacja obrazu",
    defaultResolutionHint: "Ustawienie ogólne dla całej aplikacji. Można je nadpisać per tryb generacji przy konfiguracji zlecenia.",
    imageOrientation: "Orientacja obrazu", imageResolution: "Rozdzielczość obrazu",
    captionLanguage: "Język napisów", captionLanguageField: "Język tekstów generowanych na obrazach",
    qa: "Kontrola jakości (QA)", fidelityScore: "Ocena wierności AI",
    fidelityHint: "Każda grafika jest oceniana względem zdjęć referencyjnych. Ocena i uwagi pojawiają się na kafelku wyniku.",
    judgeModel: "Model sędziego", autoCorrect: "Auto-korekta odrzuconych",
    autoCorrectHint: "Odrzucona grafika jest raz regenerowana z uwagami sędziego, zanim trafi do Ciebie. Zwiększa koszt — każda korekta to dodatkowa generacja.",
    landingPage: "Strona główna", landingVisible: "Strona główna widoczna",
    landingHint: "Gdy wyłączona, wejście na \"/\" automatycznie przekierowuje do aplikacji.",
    productCard: "Karta produktu", step2Review: "KROK 02: Sprawdź kartę produktu",
    step2Hint: "Po wyłączeniu workflow po wgraniu danych przechodzi od razu do wyboru typów grafik.",
    brands: "Marki", brandsVisible: "Moduł marek widoczny",
    brandsHint: "Po wyłączeniu pozycja „Marki” znika z menu i z formularzy generacji.",
    catalogs: "Katalogi", catalogsVisible: "Moduł katalogów widoczny",
    catalogsHint: "Po wyłączeniu pozycja „Katalogi” znika z menu, a dostęp do katalogów jest zablokowany.",
    urlImport: "Import z linku", urlImportVisible: "Import produktu z linku",
    urlImportHint: "Po wyłączeniu zakładka „Link” znika z kroku wgrywania danych, a pobieranie zdjęć i opisów ze stron jest zablokowane.",
    saving: "Zapisuję…", saveSettings: "Zapisz ustawienia", managePrompts: "Zarządzaj promptami →",
  },
  en: {
    title: "Settings", textModel: "Text model", textModelField: "Model for product parsing",
    imageModel: "Image model", imageModelField: "Model for generating visuals",
    defaultResolution: "Default image resolution and orientation",
    defaultResolutionHint: "A general setting for the whole app. It can be overridden per generation mode when configuring a job.",
    imageOrientation: "Image orientation", imageResolution: "Image resolution",
    captionLanguage: "Caption language", captionLanguageField: "Language of text rendered on the images",
    qa: "Quality control (QA)", fidelityScore: "AI fidelity scoring",
    fidelityHint: "Each visual is scored against the reference photos. The score and notes appear on the result tile.",
    judgeModel: "Judge model", autoCorrect: "Auto-correct rejected",
    autoCorrectHint: "A rejected visual is regenerated once with the judge's notes before it reaches you. Increases cost — each correction is an extra generation.",
    landingPage: "Landing page", landingVisible: "Landing page visible",
    landingHint: "When disabled, visiting \"/\" redirects to the app automatically.",
    productCard: "Product card", step2Review: "STEP 02: Review the product card",
    step2Hint: "When disabled, after uploading data the workflow jumps straight to choosing visual types.",
    brands: "Brands", brandsVisible: "Brands module visible",
    brandsHint: "When disabled, the \"Brands\" item disappears from the menu and generation forms.",
    urlImport: "Link import", urlImportVisible: "Import product from a link",
    urlImportHint: "When disabled, the \"Link\" tab disappears from the upload step and fetching images and descriptions from web pages is blocked.",
    catalogs: "Catalogs", catalogsVisible: "Catalogs module visible",
    catalogsHint: "When disabled, the \"Catalogs\" item disappears from the menu and access to catalogs is blocked.",
    saving: "Saving…", saveSettings: "Save settings", managePrompts: "Manage prompts →",
  },
};

type Model = { id: string; label: string };

type Props = {
  current: {
    text_model: string;
    image_model: string;
    judge_model: string;
    output_language: string;
    default_aspect_ratio: string;
    default_image_size: string;
    quality_gate_enabled: boolean;
    quality_gate_retry_enabled: boolean;
    landing_page_enabled: boolean;
    product_card_review_enabled: boolean;
    brands_enabled: boolean;
    catalogs_enabled: boolean;
    url_import_enabled: boolean;
  };
  text_models: Model[];
  image_models: Model[];
  judge_models: Model[];
  output_languages: Model[];
  image_aspect_ratios: Model[];
  image_sizes: Model[];
  products?: { id: number; name: string }[];
  current_user: { name: string; email: string; role: string };
  flash_error?: string;
};

export default function Settings({ current, text_models, image_models, judge_models, output_languages, image_aspect_ratios, image_sizes, products = [], current_user, flash_error }: Props) {
  const t = pick(copy);
  const { data, setData, patch, processing } = useForm({
    text_model: current.text_model,
    image_model: current.image_model,
    judge_model: current.judge_model,
    output_language: current.output_language,
    default_aspect_ratio: current.default_aspect_ratio,
    default_image_size: current.default_image_size,
    quality_gate_enabled: current.quality_gate_enabled,
    quality_gate_retry_enabled: current.quality_gate_retry_enabled,
    landing_page_enabled: current.landing_page_enabled,
    product_card_review_enabled: current.product_card_review_enabled,
    brands_enabled: current.brands_enabled,
    catalogs_enabled: current.catalogs_enabled,
    url_import_enabled: current.url_import_enabled,
  });

  const initials = current_user.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  function submit(e: React.FormEvent) {
    e.preventDefault();
    patch("/settings");
  }

  return (
    <AppShell
      products={products}
      user={{ name: current_user.name, initials, role: current_user.role }}
    >
      <div className="page" style={{ maxWidth: 560 }}>
        <div className="page-hd">
          <div><h2>{t.title}</h2></div>
        </div>

        {flash_error && (
          <div style={{ marginBottom: 14, padding: "8px 12px", background: "oklch(0.62 0.20 25 / 0.08)", border: "1px solid oklch(0.62 0.20 25 / 0.3)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--danger)" }}>
            {flash_error}
          </div>
        )}

        <form onSubmit={submit}>
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.textModel}</h3></div>
            <div className="panel-body">
              <div className="field">
                <label>{t.textModelField}</label>
                <select
                  className="input"
                  value={data.text_model}
                  onChange={(e) => setData("text_model", e.target.value)}
                >
                  {text_models.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.imageModel}</h3></div>
            <div className="panel-body">
              <div className="field">
                <label>{t.imageModelField}</label>
                <select
                  className="input"
                  value={data.image_model}
                  onChange={(e) => setData("image_model", e.target.value)}
                >
                  {image_models.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.defaultResolution}</h3></div>
            <div className="panel-body">
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
                {t.defaultResolutionHint}
              </div>
              <div className="field">
                <label>{t.imageOrientation}</label>
                <select
                  className="input"
                  value={data.default_aspect_ratio}
                  onChange={(e) => setData("default_aspect_ratio", e.target.value)}
                >
                  {image_aspect_ratios.map((ratio) => (
                    <option key={ratio.id} value={ratio.id}>{ratio.label}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ marginTop: 16 }}>
                <label>{t.imageResolution}</label>
                <select
                  className="input"
                  value={data.default_image_size}
                  onChange={(e) => setData("default_image_size", e.target.value)}
                >
                  {image_sizes.map((size) => (
                    <option key={size.id} value={size.id}>{size.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.captionLanguage}</h3></div>
            <div className="panel-body">
              <div className="field">
                <label>{t.captionLanguageField}</label>
                <select
                  className="input"
                  value={data.output_language}
                  onChange={(e) => setData("output_language", e.target.value)}
                >
                  {output_languages.map((language) => (
                    <option key={language.id} value={language.id}>{language.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.qa}</h3></div>
            <div className="panel-body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.fidelityScore}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {t.fidelityHint}
                  </div>
                </div>
                <Switch
                  label={t.fidelityScore}
                  checked={data.quality_gate_enabled}
                  onChange={(value) => setData("quality_gate_enabled", value)}
                />
              </div>

              {data.quality_gate_enabled && (
                <>
                  <div className="field" style={{ marginTop: 16 }}>
                    <label>{t.judgeModel}</label>
                    <select
                      className="input"
                      value={data.judge_model}
                      onChange={(e) => setData("judge_model", e.target.value)}
                    >
                      {judge_models.map((model) => (
                        <option key={model.id} value={model.id}>{model.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 16 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.autoCorrect}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                        {t.autoCorrectHint}
                      </div>
                    </div>
                    <Switch
                      label={t.autoCorrect}
                      checked={data.quality_gate_retry_enabled}
                      onChange={(value) => setData("quality_gate_retry_enabled", value)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.landingPage}</h3></div>
            <div className="panel-body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.landingVisible}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {t.landingHint}
                  </div>
                </div>
                <Switch
                  label={t.landingVisible}
                  checked={data.landing_page_enabled}
                  onChange={(value) => setData("landing_page_enabled", value)}
                />
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.productCard}</h3></div>
            <div className="panel-body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.step2Review}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {t.step2Hint}
                  </div>
                </div>
                <Switch
                  label={t.step2Review}
                  checked={data.product_card_review_enabled}
                  onChange={(value) => setData("product_card_review_enabled", value)}
                />
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.brands}</h3></div>
            <div className="panel-body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.brandsVisible}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {t.brandsHint}
                  </div>
                </div>
                <Switch
                  label={t.brandsVisible}
                  checked={data.brands_enabled}
                  onChange={(value) => setData("brands_enabled", value)}
                />
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.catalogs}</h3></div>
            <div className="panel-body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.catalogsVisible}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {t.catalogsHint}
                  </div>
                </div>
                <Switch
                  label={t.catalogsVisible}
                  checked={data.catalogs_enabled}
                  onChange={(value) => setData("catalogs_enabled", value)}
                />
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.urlImport}</h3></div>
            <div className="panel-body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.urlImportVisible}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {t.urlImportHint}
                  </div>
                </div>
                <Switch
                  label={t.urlImportVisible}
                  checked={data.url_import_enabled}
                  onChange={(value) => setData("url_import_enabled", value)}
                />
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={processing}>
            {processing ? t.saving : t.saveSettings}
          </button>

          <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--line)" }}>
            <a href="/prompt_templates" className="btn btn-sm btn-ghost">
              {t.managePrompts}
            </a>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
